/**
 * The words that travel with an invite link (onboarding plan, slice 1) — the
 * same line on WhatsApp, the share sheet and the clipboard.
 */

export const MAIL_DOMAIN = 'toiyp.me';

/** `grace` → `grace@toiyp.me` — the address a handle doubles as. */
export function toiypAddress(handle: string): string {
  return `${handle}@${MAIL_DOMAIN}`;
}

/**
 * What the handle box will become, as typed: trimmed, without a leading `@`,
 * lower-cased (handles are case-insensitive).
 */
export function handleFromInput(raw: string): string {
  return raw.trim().replace(/^@+/, '').toLowerCase();
}

export function inviteShareText(handle: string, url: string): string {
  return `Don't text me, toiyp me ✉️ I'm ${toiypAddress(handle)}. Write me a proper letter here: ${url}`;
}

/** WhatsApp's universal link: opens the app (or web) with the text ready, recipient unchosen. */
export function whatsAppShareUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

/**
 * Where an invite link was handed out (slice 3). Tagged onto the link as
 * `via=…` so an arrival can say which channel brought it; slice 4 stores it
 * for referral attribution.
 */
export type Via = 'wa' | 'sheet' | 'copy' | 'qr' | 'story' | 'bio' | 'delivered_prompt';

export const VIAS: readonly Via[] = ['wa', 'sheet', 'copy', 'qr', 'story', 'bio', 'delivered_prompt'];

export function isVia(v: unknown): v is Via {
  return typeof v === 'string' && (VIAS as readonly string[]).includes(v);
}

/** The link token and channel tag in a query string (`?link=…&via=…`). */
export function readInviteParams(search: string): { token: string | null; via: Via | null } {
  const params = new URLSearchParams(search);
  const via = params.get('via');
  return { token: params.get('link'), via: isVia(via) ? via : null };
}

/** The invite link, tagged with the channel it travels by (replacing any old tag). */
export function withVia(url: string, via: Via): string {
  try {
    const u = new URL(url);
    u.searchParams.set('via', via);
    return u.toString();
  } catch {
    return `${url}${url.includes('?') ? '&' : '?'}via=${via}`;
  }
}

export type BioPlatform = 'instagram' | 'x' | 'linkedin';

/** A line for each kind of bio, and where each app edits it (when it has a web page for that). */
export const BIO_LINES: readonly {
  platform: BioPlatform;
  label: string;
  line: (handle: string) => string;
  edit: { label: string; url: string } | null;
}[] = [
  {
    platform: 'instagram',
    label: 'Instagram / TikTok',
    line: (h) => `✉️ toiyp me: ${toiypAddress(h)}`,
    edit: { label: 'Edit your Instagram bio', url: 'https://www.instagram.com/accounts/edit/' },
  },
  {
    platform: 'x',
    label: 'X / Threads',
    line: (h) => `Don't text me, toiyp me → ${toiypAddress(h)}`,
    edit: { label: 'Edit your X profile', url: 'https://x.com/settings/profile' },
  },
  {
    platform: 'linkedin',
    label: 'LinkedIn',
    line: (h) => `Prefer a proper letter? ${toiypAddress(h)}`,
    edit: { label: 'Edit your LinkedIn intro', url: 'https://www.linkedin.com/in/me/edit/intro/' },
  },
];
